import PropTypes from "prop-types";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import Img from "gatsby-image";
import { Link } from "gatsby";
import { AppContext } from "~context/AppContext.jsx";
import SquiggleLine from "~components/svg/SquiggleLine";
import Button from "../Button";
import { DocumentContext } from "~context/DocumentContext";
import Carousel from "~components/Carousel";
import { getVariantByOptions } from "~utils/shopify";

const CollectionGrid = ({
  data,
  title,
  items,
  displayMode,
  defCategory,
  setChart,
  ...props
}) => {
  const { addToCart, inventoryCache, inventoryFetched } = useContext(
    AppContext
  );
  const { device, windowWidth } = useContext(DocumentContext);

  const productsRef = useRef();

  const [selectedCategory, setSelectedCategory] = useState(defCategory);
  const [addEnabledProducts, setAddEnabledProducts] = useState({});

  const enableProductAdd = (handle, colour) => {
    const enabled = JSON.parse(JSON.stringify(addEnabledProducts));

    enabled[handle] = colour;

    setAddEnabledProducts(enabled);
  };

  const parseToId = key => {
    return key
      .toLowerCase()
      .replace(/ /g, `-`)
      .replace(/’/g, `-`);
  };

  const showMobileCarousel = device === `mobile` && displayMode === `carousel`;
  const showDesktopCarousel = device !== `mobile` && displayMode === `carousel`;

  const scrollToItems = () => {
    if (!window || typeof window === `undefined`) {
      return;
    }

    const top =
      productsRef.current.offsetTop -
      (device === `desktop` ? windowWidth * 0.02 : 50);

    window.scrollTo({
      top,
      behavior: `smooth`
    });
  };

  const handleCategoryClick = category => {
    if (category === selectedCategory) {
      scrollToItems();
    } else {
      setSelectedCategory(category);
    }
  };

  const categoryObject = useMemo(() => {
    const obj = items.find(
      item => item.title === selectedCategory && item.products.length > 0
    );

    if (!obj) {
      return obj;
    }

    const groups = {};

    obj.products.forEach(product => {
      if (!Object.prototype.hasOwnProperty.call(groups, product.title)) {
        groups[product.title] = [];
      }

      groups[product.title].push(product);
    });

    return {
      ...obj,
      productGroups: groups
    };
  }, [selectedCategory]);

  useEffect(() => {
    if (categoryObject) {
      scrollToItems();
    }
  }, [categoryObject]);

  return (
    <div {...props}>
      {title?.length > 0 && (
        <header className="grid">
          <div className="grid-end-12">
            <h1 className="f3">{title}</h1>
            <div style={device !== `mobile` ? { width: 317 } : null}>
              <SquiggleLine className="w-full mt-v2 xs:mt-6 mb-v3 xs:mb-8" />
            </div>
          </div>
        </header>
      )}

      {!showMobileCarousel && !showDesktopCarousel && (
        <ul className="grid gap-col-2 xs:gap-row-2">
          {items.map(item => (
            <li
              key={item.title}
              className="collection-grid__item grid-end-3 xs:grid-end-12 mb-12 xs:mb-12"
            >
              <div className="collection-grid__item__image relative block rounded-edge overflow-hidden">
                {(item.image?.asset?.fluid && (
                  <figure className="w-full h-full absolute top-0 right-0 bottom-0 left-0 gpu">
                    <Img
                      className="w-full h-full relative object-cover"
                      fluid={item.image?.asset?.fluid}
                      alt={item.title}
                    />
                  </figure>
                )) || (
                  <img
                    className="w-full absolute transform-center"
                    src="/uploads/sample.jpg"
                    alt={item.title}
                  />
                )}
              </div>

              <article className="mt-v05 xs:mt-5">
                <header className="w-full relative flex items-center justify-between pt-v1">
                  <h2 className="f4 font-bold">{item.title}</h2>
                </header>

                <div className="mt-v2 xs:mt-4">
                  {item.link?.length > 0 ? (
                    <Link to={item.link}>
                      <Button
                        className="w-full relative block xs:py-2"
                        color="hit-pink"
                        transparent
                      >
                        <span className="f-button">Shop Now</span>
                      </Button>
                    </Link>
                  ) : (
                    <Button
                      className="w-full relative block xs:py-2"
                      color="hit-pink"
                      transparent
                      onClick={() => handleCategoryClick(item.title)}
                    >
                      <span className="f-button">Shop Now</span>
                    </Button>
                  )}
                </div>
              </article>
            </li>
          ))}
        </ul>
      )}
      {showMobileCarousel && (
        <Carousel
          keyPrefix={title.replace(/[^A-Za-z]/g, ``).toLowerCase()}
          gap={10}
          peek={20}
          type="slider"
          bound
          rewind
          items={items.map(item => (
            <div key={item.title} className="collection-grid__item xs:mb-8">
              <div
                className="collection-grid__item__image relative block rounded-edge overflow-hidden bg-center bg-no-repeat bg-cover"
                style={{
                  backgroundImage: `url(${item.image?.asset?.fluid?.src ||
                    `/uploads/sample.jpg`})`
                }}
              />

              <article className="mt-v05 xs:mt-5">
                <header className="w-full relative flex items-center justify-between pt-v1">
                  <h2 className="f4 font-bold">{item.title}</h2>
                </header>

                <div className="mt-v2 xs:mt-4">
                  {item.link?.length > 0 ? (
                    <Link to={item.link}>
                      <Button
                        className="w-full relative block xs:py-2"
                        color="hit-pink"
                        transparent
                      >
                        <span className="f-button">Shop Now</span>
                      </Button>
                    </Link>
                  ) : (
                    <Button
                      className="w-full relative block xs:py-2"
                      color="hit-pink"
                      transparent
                      onClick={() => handleCategoryClick(item.title)}
                    >
                      <span className="f-button">Shop Now</span>
                    </Button>
                  )}
                </div>
              </article>
            </div>
          ))}
        />
      )}
      {showDesktopCarousel && (
        <div className="carousel-grid grid">
          <Carousel
            className="grid-end-12 mb-10"
            keyPrefix={title.replace(/[^A-Za-z]/g, ``).toLowerCase()}
            perView={4}
            type="slider"
            bound
            rewind
            withBullets
            items={items.map(item => (
              <div
                key={item.title}
                className="collection-grid__item xs:mb-4 px-v1"
              >
                <div
                  className="collection-grid__item__image relative block rounded-edge overflow-hidden bg-center bg-no-repeat bg-cover"
                  style={{
                    backgroundImage: `url(${item.image?.asset?.fluid?.src ||
                    `/uploads/sample.jpg`})`
                  }}
                />

                <article className="mt-v05 xs:mt-5 mb-1">
                  <header className="w-full relative flex items-center justify-between pt-v1">
                    <h2 className="f4 font-bold">{item.title}</h2>
                  </header>

                  <div className="mt-v2 xs:mt-4">
                    {item.link?.length > 0 ? (
                      <Link to={item.link}>
                        <Button
                          className="w-full relative block xs:py-2"
                          color="hit-pink"
                          transparent
                        >
                          <span className="f-button">Shop Now</span>
                        </Button>
                      </Link>
                    ) : (
                      <Button
                        className="w-full relative block xs:py-2"
                        color="hit-pink"
                        transparent
                        onClick={() => handleCategoryClick(item.title)}
                      >
                        <span className="f-button">Shop Now</span>
                      </Button>
                    )}
                  </div>
                </article>
              </div>
            ))}
          />
        </div>
      )}

      {categoryObject && (
        <div ref={productsRef} className="grid">
          {Object.keys(categoryObject.productGroups).map(subcategory => (
            <>
              <div className="animation-appear-left-slow animation-delay-1 grid-end-3 xs:grid-end-12 xs:h-auto xs:relative top-0 xs:top-auto left-0 xs:left-auto pt-v2 xs:pt-0 xs:pb-v4">
                <h1 className="f3">{subcategory}</h1>

                <SquiggleLine className="w-full mt-v1 xs:mt-6" />

                {false && (
                  <button
                    type="button"
                    className={`mt-v1 xs:mt-2 ${
                      device === `desktop` ? `caption` : `b2`
                    } cursor-pointer underline`}
                    onClick={() => setChart(true)}
                  >
                    Unisex Size Chart
                  </button>
                )}
              </div>

              <div className="animation-appear-right-slow animation-delay-1 grid-end-9 xs:grid-end-12 relative pt-v2">
                <section
                  id={parseToId(categoryObject.title)}
                  className="relative flex flex-wrap mb-v4"
                >
                  {categoryObject.productGroups[subcategory].map(
                    (product, productIndex) => {
                      const key = product.handle;

                      // variant buttons

                      const colourButtons = [];
                      const sizeButtons = [];

                      product.shopifyProduct.variants.forEach(variant => {
                        const options = variant.selectedOptions;

                        options.forEach(option => {
                          switch (option.name.toLowerCase()) {
                            case `color`:
                            case `colour`:
                              if (!colourButtons.includes(option.value)) {
                                colourButtons.push(option.value);
                              }

                              break;

                            case `size`:
                              if (!sizeButtons.includes(option.value)) {
                                sizeButtons.push(option.value);
                              }

                              break;

                            default:
                              break;
                          }
                        });
                      });

                      // variant buttons (colours)

                      const colourButtonJSX = [];

                      let backgroundStyle = {};

                      colourButtons.forEach(colourValue => {
                        const selected =
                          addEnabledProducts?.[product.handle] === colourValue;

                        let hexes;

                        data.allSanityColourMap.edges.forEach(({ node }) => {
                          if (hexes) {
                            return;
                          }

                          if (
                            node.title.toLowerCase() ===
                            colourValue.toLowerCase()
                          ) {
                            ({ hexes } = node);
                          }
                        });

                        if (hexes?.[0]) {
                          if (hexes.length === 3) {
                            backgroundStyle = {
                              background: `linear-gradient(to right, ${hexes[0]} 33.333%, ${hexes[1]} 33.333%, ${hexes[1]} 66.667%, ${hexes[2]} 66.667%)`
                            };
                          } else if (hexes.length === 2) {
                            backgroundStyle = {
                              background: `linear-gradient(to right, ${hexes[0]} 50%, ${hexes[1]} 50%)`
                            };
                          } else {
                            backgroundStyle = {
                              backgroundColor: hexes[0]
                            };
                          }
                        }

                        colourButtonJSX.push(
                          <button
                            key={`${product.handle}-${colourValue}`}
                            type="button"
                            className={`product-page__color-picker w-8 h-8 mr-2 xs:mr-3 relative block rounded-full ${
                              selected ? `border-black` : ``
                            }`}
                            onClick={() =>
                              enableProductAdd(product.handle, colourValue)
                            }
                          >
                            <div
                              className="product-page__color-picker__bg w-full h-full relative rounded-full overflow-hidden pointer-events-none"
                              style={backgroundStyle}
                            />
                          </button>
                        );
                      });

                      // variant buttons (sizes)

                      const sizeButtonJSX = [];

                      sizeButtons.forEach(sizeValue => {
                        let buttonText;

                        switch (sizeValue.toLowerCase()) {
                          case `s`:
                            buttonText = `SML`;
                            break;

                          case `m`:
                            buttonText = `MED`;
                            break;

                          case `l`:
                            buttonText = `LRG`;
                            break;

                          case `xl`:
                          default:
                            buttonText = `null`;

                            break;
                        }

                        let variantForSize;
                        let inStock = false;

                        if (
                          addEnabledProducts?.[product.handle] &&
                          inventoryFetched
                        ) {
                          variantForSize = getVariantByOptions(
                            product.shopifyProduct,
                            {
                              Colour: addEnabledProducts?.[product.handle],
                              Size: sizeValue
                            }
                          );

                          inStock =
                            variantForSize?.id &&
                            inventoryCache?.[variantForSize.id] &&
                            inventoryCache[variantForSize.id] > 0;
                        }

                        if (buttonText != `null`) {
                          sizeButtonJSX.push(
                            <Button
                              className={`${
                                inStock ? `` : `opacity-25 pointer-events-none`
                              } relative mr-3 pad-0 display-unset`}
                              onClick={() => {
                                if (variantForSize) {
                                  addToCart({
                                    product: product.shopifyProduct,
                                    variant: variantForSize,
                                    variants: null
                                  });
                                }
                              }}
                              color="hit-pink"
                              transparent
                            >
                              <span
                                className={`${
                                  device === `desktop`
                                    ? `f-button`
                                    : `b2 italic font-10`
                                }`}
                              >
                                {buttonText}
                              </span>
                            </Button>
                          );
                        }
                      });

                      //
                      // button-based image parsing

                      let image;
                      let imageType = `default`;

                      if (!addEnabledProducts?.[product.handle]) {
                        // if the user hasn't clicked a colour button in this item
                        if (product?.image?.asset?.fluid) {
                          image = product.image.asset.fluid;
                          imageType = `fluid`;
                        } else if (product?.images?.[1]?.originalSrc) {
                          image = product?.images?.[1]?.originalSrc;
                        }
                      } else {
                        // else the user has selected something and we should change the image to suit
                        const colourKey = addEnabledProducts[product.handle];
                        let fallbackImage;

                        imageType = `fluid`;

                        if (product?.availableColours?.[0]) {
                          product.availableColours.forEach(
                            ({ colourRef, gallery }) => {
                              if (image) {
                                return;
                              }

                              if (
                                !fallbackImage &&
                                gallery?.[0]?.asset?.fluid
                              ) {
                                fallbackImage = gallery[0].asset.fluid;
                              }

                              if (
                                colourRef?.title === colourKey &&
                                gallery?.[0]
                              ) {
                                image = gallery[0].asset.fluid;
                              }
                            }
                          );
                        }

                        if (!image) {
                          image = fallbackImage;
                        }
                      }

                      const imageJSX = (
                        <Link
                          to={`/products/${product.handle}`}
                          className="product-grid__item__image relative block rounded-edge overflow-hidden"
                        >
                          {(imageType === `fluid` && (
                            <figure className="w-full h-full absolute top-0 right-0 bottom-0 left-0 gpu">
                              <Img
                                className="w-full h-full relative object-cover"
                                fluid={image}
                                alt={product.handle}
                              />
                            </figure>
                          )) || (
                            <img
                              className="w-full absolute transform-center"
                              src={image}
                              alt={product.handle}
                            />
                          )}
                        </Link>
                      );

                      const variantSelector = (
                        <>
                          <div className="w-full flex flex-wrap mt-v1 mb-v1">
                            {colourButtonJSX}
                          </div>

                          {false && (
                            <>
                              {(!addEnabledProducts?.[product.handle] && (
                                <></>
                              )) || (
                                <div className="products-page__hover-tray animation-appear w-full relative block min-height-46">
                                  <div className="products-page__hover-tray__shelf w-full absolute xs:relative top-0 xs:top-auto right-0 xs:right-auto bottom-0 xs:bottom-auto left-0 xs:left-auto z-10 justify-between xs:mb-2 height-30">
                                    {sizeButtonJSX}
                                  </div>

                                  {(device === `desktop` && (
                                    <Button
                                      className="products-page__hover-tray__button w-full relative block pointer-events-none"
                                      color="hit-pink"
                                      transparent
                                    >
                                      <span className="f-button">
                                        Add to bag
                                      </span>
                                    </Button>
                                  )) || <></>}
                                </div>
                              )}
                            </>
                          )}
                        </>
                      );

                      //

                      const innerJSX = (
                        <>
                          <>{imageJSX}</>

                          <article className="mt-v05">
                            <header className="w-full relative pt-v1 grid-display">
                              <div className="inline-block mb-2 sm-m5">
                                <h2 className="f5 font-bold float-left mr-3 xs:mt-2 xs:mb-1">
                                  {product.packSize || product.title}
                                </h2>
                              </div>

                              {false && (
                                <div className="inline-block items-center">
                                  {product?.shopifyProduct?.variants?.[0]
                                    ?.price && (
                                    <h2 className="b1 float-left mr-2 font-15">
                                      $
                                      {product.shopifyProduct.variants[0].price}
                                    </h2>
                                  )}
                                  {product?.shopifyProduct?.variants?.[0]
                                    ?.compareAtPrice && (
                                    <strike className="float-left">
                                      <h3 className="b1 font-15">
                                        $
                                        {
                                          product.shopifyProduct.variants[0]
                                            .compareAtPrice
                                        }
                                      </h3>
                                    </strike>
                                  )}
                                </div>
                              )}
                            </header>

                            {variantSelector}

                            <Link to={`/products/${product.handle}`}>
                              <Button
                                className="products-page__hover-tray__button w-full relative block xs:mt-2"
                                color="hit-pink"
                                transparent
                              >
                                <span className="f-button">Shop now</span>
                              </Button>
                            </Link>
                          </article>
                        </>
                      );

                      const style =
                        device === `mobile`
                          ? {
                              paddingLeft: `${
                                productIndex % 2 === 0 ? 0 : 1
                              }vw`,
                              paddingRight: `${
                                productIndex % 2 === 0 ? 1 : 0
                              }vw`
                            }
                          : {
                              paddingLeft: `${
                                productIndex % 3 === 0
                                  ? 0
                                  : productIndex % 3 === 1
                                  ? 0.666
                                  : 1.333
                              }vw`,
                              paddingRight: `${
                                productIndex % 3 === 0
                                  ? 1.333
                                  : productIndex % 3 === 1
                                  ? 0.666
                                  : 0
                              }vw`
                            };

                      return (
                        <div
                          key={key}
                          className={`${
                            device === `mobile` ? `w-full` : `w-1/3`
                          } relative mb-v2 xs:mb-v12 pb-v1`}
                          style={style}
                        >
                          {innerJSX}
                        </div>
                      );
                    }
                  )}
                </section>
              </div>
            </>
          ))}
        </div>
      )}
    </div>
  );
};

CollectionGrid.defaultProps = {
  data: {},
  title: ``,
  items: [],
  displayMode: `carousel`,
  defCategory: null,
  setChart: () => null
};

CollectionGrid.propTypes = {
  data: PropTypes.objectOf(PropTypes.any),
  title: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.any),
  displayMode: PropTypes.string,
  defCategory: PropTypes.string,
  setChart: PropTypes.func
};

export default CollectionGrid;
